import React from 'react'
import { ApolloError } from 'apollo-client'
import { CircleLoader, FullPageLoader } from '@deal/components'
import { createContentfulObject, getSpaceFromTemplate, ContentfulTemplateMap } from '#src/services/contentful'
import config from '#src/config'
import {
  ContentfulByVariationComponent
} from '#src/generated/graphql'
import { HostnameConsumer, Hostname } from '../Hostname'

type IsFunction<T> = T extends (...args: any[]) => any ? T : never
const isFunction = <T extends {}>(value: T): value is IsFunction<T> => typeof value === 'function'

export interface ContentfulImage {
  mimeType: string
  title: string
  imageUrl: string
}

type ContentResponse = {
  [key: string]: any
} | null

type ContentContext = {
  [key: string]: any
}

type ContentfulRenderFunction = (
  result: {
    content?: ContentResponse
    context?: ContentContext
    loading: boolean
    error?: ApolloError
  }
) => React.ReactNode

interface ContentfulProps {
  space?: string
  template: keyof ContentfulTemplateMap
  variation: string
  children: ContentfulRenderFunction | React.ReactNode
}

const ContentfulQuery: React.SFC<ContentfulProps> = ({
  space,
  template,
  variation,
  children
}): JSX.Element => {
  let spaceId = space
  if (!spaceId) {
    spaceId = getSpaceFromTemplate(template)
  }

  return (
    <HostnameConsumer>
      {hostname => (
        <ContentfulByVariationComponent
          variables={{ template, variation, space: spaceId }}
        >
          {({ data, loading, error }) => {
            if (data && data.findContentByTemplate !== undefined) {
              const isLandingPageQuery = ['northstar', 'alta', 'landingPage'].includes(template)

              /**
               * If this is a landing-page query, recurse through the response and
               *   blindly replace Deal with Curated 🙈
               */
              const transform =
                hostname === Hostname.Curated
                  ? (s: string) => s.replace(/Deal(?!s)/g, 'Curated')
                  : undefined

              /**
               * Convert the response into a more structured format
               */
              const content = data.findContentByTemplate
                ? createContentfulObject(data.findContentByTemplate.entries, transform)
                : undefined
              const context: ContentContext = {}

              // Additional context (specific only to certain children)
              if (isLandingPageQuery) {
                context['facebookAppId'] = config.get('facebook.app_id')
                context['facebookPageId'] = config.get('facebook.page_id')
              }

              if (children && isFunction(children)) {
                /**
                 * Pass data to `children` as render fn
                 **/
                return children({ content, context, loading: false })
              }

              /**
               * Pass data to `children` as `content` prop
               **/
              //@ts-ignore: SFC and cloneElement have different definitions of children
              return React.cloneElement(children, {
                content,
                ...context
              })
            } else if (loading) {
              if (children && isFunction(children)) {
                return children({ loading: true })
              }

              return <FullPageLoader loader={CircleLoader} />
            } else if (error) {
              if (children && isFunction(children)) {
                return children({ error, loading: false })
              }

              return null
            } else {
              return null
            }
          }}
        </ContentfulByVariationComponent>
      )}
    </HostnameConsumer>
  )
}

export default ContentfulQuery
